import React, { lazy, useContext } from "react";
import {
  Routes,
  Route,
  Navigate,
  BrowserRouter,
  useLocation,
} from "react-router-dom";
import { MainContext } from "../contexts/MainContext";

const Home = lazy(() => import("../pages/Home"));
const Capital = lazy(() => import("../pages/Capital"));
const CapitalDetails = lazy(() => import("../pages/CapitalDetails"));
const Expenses = lazy(() => import("../pages/Expenses"));
const SavingsSources = lazy(() => import("../pages/SavingsSources"));
const ExpensesLists = lazy(() => import("../pages/ExpensesLists"));
const SavingsSource = lazy(() => import("../pages/SavingsSource"));
const EncryptedNotePage = lazy(() => import("../pages/EncryptedNotePage"));
const Settings = lazy(() => import("../pages/Settings"));
const Categories = lazy(() => import("../pages/Categories"));
const CustomStocks = lazy(() => import("../pages/CustomStocks"));
const Profile = lazy(() => import("../pages/Profile"));
const Groups = lazy(() => import("../pages/Groups"));

const PrivateRoute = ({ children }) => {
  const { jwtToken, currentGroupId } = useContext(MainContext);
  const location = useLocation();
  const pathname = location.pathname;

  // First check if user is authenticated
  if (!jwtToken) {
    return <Navigate to="/session/new" />;
  }

  // Then check if there's a currentGroupId
  if (!currentGroupId) {
    // Allow access to profile page
    if (pathname === "/new_profile") {
      return children;
    }

    // Don't redirect if already on settings page
    if (pathname === "/new_settings/groups") {
      return children;
    }

    // Redirect to settings for all other pages
    return <Navigate to="/new_settings/groups" />;
  }

  // If authenticated and has currentGroupId, allow access
  return children;
};

const AppRouter = () => (
  <BrowserRouter>
    <Routes>
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_capital/details"
        element={
          <PrivateRoute>
            <CapitalDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_capital"
        element={
          <PrivateRoute>
            <Capital />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_savings_sources"
        element={
          <PrivateRoute>
            <SavingsSources />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_savings_sources/:id"
        element={
          <PrivateRoute>
            <SavingsSource />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_savings_sources/:id/encrypted_note"
        element={
          <PrivateRoute>
            <EncryptedNotePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_expenses_lists/:id"
        element={
          <PrivateRoute>
            <Expenses />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_expenses_lists"
        element={
          <PrivateRoute>
            <ExpensesLists />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_expenses/:id"
        element={
          <PrivateRoute>
            <Expenses />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_settings/groups"
        element={
          <PrivateRoute>
            <Groups />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_settings/custom_stocks"
        element={
          <PrivateRoute>
            <CustomStocks />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_settings/expense_categories"
        element={
          <PrivateRoute>
            <Categories kind="expense" />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_settings/savings_categories"
        element={
          <PrivateRoute>
            <Categories kind="savings" />
          </PrivateRoute>
        }
      />
      <Route
        path="/new_settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
    </Routes>
  </BrowserRouter>
);

export default AppRouter;
